import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CustomThemeProvider } from './context/ThemeContext';
import './i18n';
import { PrivateRoute, AdminRoute, TrainerRoute, RoleBasedRoute, PublicOnlyRoute, FacilityAccessRoute } from './components/ProtectedRoute';
import ResponsiveLayout from './components/ResponsiveLayout';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import RegistrationStatusPage from './pages/RegistrationStatusPage';
import EmailVerificationPage from './pages/EmailVerificationPage';
import EmailSentPage from './pages/EmailSentPage';
import EmailVerifiedPage from './pages/EmailVerifiedPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import PublicPlansPage from './pages/PublicPlansPage';
import PlanListPage from './pages/PlanListPage';
import ViewFacilitiesPage from './pages/ViewFacilitiesPage';
import FacilityListPage from './pages/FacilityListPage';
import FacilityAvailabilityPage from './pages/FacilityAvailabilityPage';
import MyBookingsPage from './pages/MyBookingsPage';
import ClassTypeListPage from './pages/ClassTypeListPage';
import ScheduledClassListPage from './pages/ScheduledClassListPage';
import AddScheduledClassPage from './pages/AddScheduledClassPage';
import EditScheduledClassPage from './pages/EditScheduledClassPage';
import ViewScheduledClassesPage from './pages/ViewScheduledClassesPage';
import ScheduledClassDetailsPage from './pages/ScheduledClassDetailsPage';
import TrainerProfilePage from './pages/TrainerProfilePage';
import UserListPage from './pages/UserListPage';
import AdminVerificationPage from './pages/AdminVerificationPage';
import ProfilePage from './pages/ProfilePage';
import GalleryPage from './pages/GalleryPage';
import TrainerPaymentDashboard from './components/TrainerPaymentDashboard';
import SportsDiaryPage from './pages/SportsDiaryPage';
import QuotaManagementInterface from './components/QuotaManagementInterface';
import AdminSessionQuotasPage from './pages/AdminSessionQuotasPage';
import AdminMemberTypesPage from './pages/AdminMemberTypesPage';
import AdminPaymentsPage from './pages/AdminPaymentsPage';
import AdminUserPlanEditorPage from './pages/AdminUserPlanEditorPage';
import SystemSettingsPage from './pages/SystemSettingsPage';
import AdminBookingsPage from './pages/AdminBookingsPage';
import AdminActivityLogsPage from './pages/AdminActivityLogsPage';
import NotificationsPage from './pages/NotificationsPage';
import ReceptionistBookingsPage from './pages/ReceptionistBookingsPage';
import ReceptionistCreateBookingPage from './pages/ReceptionistCreateBookingPage';
import ReceptionistRegistrationPage from './pages/ReceptionistRegistrationPage';
import ReceptionistPaymentsPage from './pages/ReceptionistPaymentsPage';
import ReceptionistMembersPage from './pages/ReceptionistMembersPage';
import KvkkManagement from './components/KvkkManagement';
import PlanFreezeApproval from './components/admin/PlanFreezeApproval';
import MyPlanFreezes from './components/MyPlanFreezes';
import ProfileSettings from './components/ProfileSettings';
import { isNotificationsEnabled } from './utils/featureFlags';

// Profile redirect component
function ProfileRedirect() {
  const { user } = useAuth();
  return <Navigate to={`/profile/${user.username}`} replace />;
}

// Layout wrapper component
const LayoutWrapper = ({ children }) => {
  return (
    <ResponsiveLayout>
      {children}
    </ResponsiveLayout>
  );
};

function App() {
  return (
    <CustomThemeProvider>
      <AuthProvider>
        <Router>
          <Suspense fallback={<div>Loading translations...</div>}>
            <LayoutWrapper>
              <Routes>
              {/* Public routes */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<PublicOnlyRoute><RegisterPage /></PublicOnlyRoute>} />

              {/* Email verification routes */}
              <Route path="/email-verification" element={<EmailVerificationPage />} />
              <Route path="/email-verification/sent" element={<EmailSentPage />} />
              <Route path="/email-verification/verified" element={<EmailVerifiedPage />} />
              <Route path="/email-verification/invalid" element={<EmailVerifiedPage />} />
              <Route path="/email-verification/expired" element={<EmailVerifiedPage />} />
              <Route path="/email-verification/used" element={<EmailVerifiedPage />} />

              {/* Password reset routes */}
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

              <Route path="/plans" element={<PublicPlansPage />} />
              <Route path="/facilities" element={<FacilityAccessRoute><ViewFacilitiesPage /></FacilityAccessRoute>} />
              <Route path="/facilities/:facilityId/book" element={<FacilityAccessRoute><FacilityAvailabilityPage /></FacilityAccessRoute>} />
              <Route path="/classes" element={<FacilityAccessRoute><ViewScheduledClassesPage /></FacilityAccessRoute>} />
              <Route path="/classes/:id" element={<FacilityAccessRoute><ScheduledClassDetailsPage /></FacilityAccessRoute>} />
              <Route path="/unauthorized" element={<UnauthorizedPage />} />

              {/* Protected routes */}
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <DashboardPage />
                  </PrivateRoute>
                }
              />
              {/* Receptionist routes */}
              <Route
                path="/receptionist/bookings"
                element={
                  <RoleBasedRoute allowedRoles={['receptionist','admin']}>
                    <ReceptionistBookingsPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/receptionist/bookings/create"
                element={
                  <RoleBasedRoute allowedRoles={['receptionist','admin']}>
                    <ReceptionistCreateBookingPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/receptionist/registration"
                element={
                  <RoleBasedRoute allowedRoles={['receptionist','admin']}>
                    <ReceptionistRegistrationPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/receptionist/payments"
                element={
                  <RoleBasedRoute allowedRoles={['receptionist','admin']}>
                    <ReceptionistPaymentsPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/receptionist/members"
                element={
                  <RoleBasedRoute allowedRoles={['receptionist','admin']}>
                    <ReceptionistMembersPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/registration-status"
                element={
                  <PrivateRoute>
                    <RegistrationStatusPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/my-bookings"
                element={
                  <FacilityAccessRoute>
                    <MyBookingsPage />
                  </FacilityAccessRoute>
                }
              />
              <Route
                path="/my-plan-freezes"
                element={
                  <PrivateRoute>
                    <MyPlanFreezes />
                  </PrivateRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <PrivateRoute>
                    <ProfileRedirect />
                  </PrivateRoute>
                }
              />
              <Route
                path="/profile/:username"
                element={
                  <PrivateRoute>
                    <ProfilePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <PrivateRoute>
                    <ProfileSettings />
                  </PrivateRoute>
                }
              />
              <Route
                path="/gallery/:galleryId"
                element={<GalleryPage />}
              />

              {/* Admin routes */}
              <Route
                path="/admin/plans"
                element={
                  <AdminRoute>
                    <PlanListPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/facilities"
                element={
                  <AdminRoute>
                    <FacilityListPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/class-types"
                element={
                  <AdminRoute>
                    <ClassTypeListPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/scheduled-classes"
                element={
                  <AdminRoute>
                    <ScheduledClassListPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/scheduled-classes/add"
                element={
                  <AdminRoute>
                    <AddScheduledClassPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/scheduled-classes/edit/:id"
                element={
                  <AdminRoute>
                    <EditScheduledClassPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <AdminRoute>
                    <UserListPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/verification"
                element={
                  <RoleBasedRoute allowedRoles={['admin', 'receptionist']}>
                    <AdminVerificationPage />
                  </RoleBasedRoute>
                }
              />

              {/* Trainer routes */}
              <Route
                path="/trainer/profile"
                element={
                  <TrainerRoute>
                    <TrainerProfilePage />
                  </TrainerRoute>
                }
              />
              <Route
                path="/trainer/payments"
                element={
                  <TrainerRoute>
                    <TrainerPaymentDashboard />
                  </TrainerRoute>
                }
              />
              <Route
                path="/sports-diary"
                element={
                  <PrivateRoute>
                    <SportsDiaryPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/quotas"
                element={
                  <AdminRoute>
                    <QuotaManagementInterface />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/session-quotas"
                element={
                  <AdminRoute>
                    <AdminSessionQuotasPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/member-types"
                element={
                  <AdminRoute>
                    <AdminMemberTypesPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/trainer-payments"
                element={
                  <AdminRoute>
                    <TrainerPaymentDashboard />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/payments"
                element={
                  <AdminRoute>
                    <AdminPaymentsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/user-plan-editor"
                element={
                  <AdminRoute>
                    <AdminUserPlanEditorPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/system-settings"
                element={
                  <AdminRoute>
                    <SystemSettingsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/plan-freezes"
                element={
                  <AdminRoute>
                    <PlanFreezeApproval />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/bookings"
                element={
                  <AdminRoute>
                    <AdminBookingsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/kvkk"
                element={
                  <AdminRoute>
                    <KvkkManagement />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/activity-logs"
                element={
                  <AdminRoute>
                    <AdminActivityLogsPage />
                  </AdminRoute>
                }
              />
              {isNotificationsEnabled() && (
                <Route
                  path="/notifications"
                  element={
                    <PrivateRoute>
                      <NotificationsPage />
                    </PrivateRoute>
                  }
                />
              )}

              {/* Catch all route */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </LayoutWrapper>
          </Suspense>
        </Router>
      </AuthProvider>
    </CustomThemeProvider>
  );
}

export default App;
